import { createSlice } from "@reduxjs/toolkit";
import {
  fetchWebsiteTemplates,
  checkSlugAvailability,
  createWebsite,
  generateWebsiteContent,
  updateWebsiteContent,
  updateWebsiteTemplate,
  markWebsitePaymentComplete,
  publishWebsite,
  quickPublishWebsite,
  editWebsite,
  fetchUserWebsites,
  fetchWebsite,
  fetchWebsiteLeads,
  deleteWebsite,
} from "./thunks";

const initialState = {
  // Templates
  templates: [],
  templatesLoading: false,

  // Slug check
  slugAvailable: null,
  slugChecking: false,

  // Current website being built
  currentWebsite: null,
  currentLoading: false,

  // AI content generation
  generating: false,

  // Publishing
  publishing: false,

  // Editing (template change + regenerate + redeploy)
  editing: false,

  // Payment
  paymentProcessing: false,

  // All user websites
  websites: [],
  websitesLoading: false,

  // Website leads
  leads: [],
  leadsLoading: false,

  // Wizard step (0-based)
  wizardStep: 0,

  // Error
  error: null,
  successMsg: null,
};

const websiteBuilderSlice = createSlice({
  name: "WebsiteBuilder",
  initialState,
  reducers: {
    setWizardStep(state, action) {
      state.wizardStep = action.payload;
    },
    clearCurrentWebsite(state) {
      state.currentWebsite = null;
      state.wizardStep = 0;
      state.slugAvailable = null;
      state.error = null;
      state.successMsg = null;
    },
    clearError(state) {
      state.error = null;
    },
    clearSuccess(state) {
      state.successMsg = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch templates
    builder
      .addCase(fetchWebsiteTemplates.pending, (state) => {
        state.templatesLoading = true;
      })
      .addCase(fetchWebsiteTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.templates = action.payload;
      })
      .addCase(fetchWebsiteTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.error = action.payload?.error || "Failed to load templates";
      });

    // Check slug
    builder
      .addCase(checkSlugAvailability.pending, (state) => {
        state.slugChecking = true;
        state.slugAvailable = null;
      })
      .addCase(checkSlugAvailability.fulfilled, (state, action) => {
        state.slugChecking = false;
        state.slugAvailable = action.payload?.available;
      })
      .addCase(checkSlugAvailability.rejected, (state) => {
        state.slugChecking = false;
        state.slugAvailable = null;
      });

    // Create website
    builder
      .addCase(createWebsite.pending, (state) => {
        state.currentLoading = true;
        state.error = null;
      })
      .addCase(createWebsite.fulfilled, (state, action) => {
        state.currentLoading = false;
        state.currentWebsite = action.payload;
        state.wizardStep = 1; // move to template selection
      })
      .addCase(createWebsite.rejected, (state, action) => {
        state.currentLoading = false;
        state.error = action.payload?.error || "Failed to create website";
      });

    // Generate content
    builder
      .addCase(generateWebsiteContent.pending, (state) => {
        state.generating = true;
        state.error = null;
      })
      .addCase(generateWebsiteContent.fulfilled, (state, action) => {
        state.generating = false;
        state.currentWebsite = action.payload;
        state.wizardStep = 3; // move to preview
      })
      .addCase(generateWebsiteContent.rejected, (state, action) => {
        state.generating = false;
        state.error = action.payload?.error || "Failed to generate content";
      });

    // Update content
    builder
      .addCase(updateWebsiteContent.fulfilled, (state, action) => {
        state.currentWebsite = action.payload;
      });

    // Update template
    builder
      .addCase(updateWebsiteTemplate.fulfilled, (state, action) => {
        state.currentWebsite = action.payload;
      });

    // Payment complete
    builder
      .addCase(markWebsitePaymentComplete.pending, (state) => {
        state.paymentProcessing = true;
      })
      .addCase(markWebsitePaymentComplete.fulfilled, (state, action) => {
        state.paymentProcessing = false;
        state.currentWebsite = action.payload;
      })
      .addCase(markWebsitePaymentComplete.rejected, (state, action) => {
        state.paymentProcessing = false;
        state.error = action.payload?.error || "Payment verification failed";
      });

    // Publish
    builder
      .addCase(publishWebsite.pending, (state) => {
        state.publishing = true;
        state.error = null;
      })
      .addCase(publishWebsite.fulfilled, (state, action) => {
        state.publishing = false;
        state.currentWebsite = action.payload;
        state.successMsg = "Website published successfully!";
        state.wizardStep = 4; // final success step
      })
      .addCase(publishWebsite.rejected, (state, action) => {
        state.publishing = false;
        state.error = action.payload?.error || "Failed to publish website";
      });

    // Fetch all websites
    builder
      .addCase(fetchUserWebsites.pending, (state) => {
        state.websitesLoading = true;
      })
      .addCase(fetchUserWebsites.fulfilled, (state, action) => {
        state.websitesLoading = false;
        state.websites = action.payload;
      })
      .addCase(fetchUserWebsites.rejected, (state, action) => {
        state.websitesLoading = false;
        state.error = action.payload?.error || "Failed to load websites";
      });

    // Fetch one website
    builder
      .addCase(fetchWebsite.pending, (state) => {
        state.currentLoading = true;
      })
      .addCase(fetchWebsite.fulfilled, (state, action) => {
        state.currentLoading = false;
        state.currentWebsite = action.payload;
      })
      .addCase(fetchWebsite.rejected, (state, action) => {
        state.currentLoading = false;
        state.error = action.payload?.error || "Website not found";
      });

    // Fetch leads
    builder
      .addCase(fetchWebsiteLeads.pending, (state) => {
        state.leadsLoading = true;
      })
      .addCase(fetchWebsiteLeads.fulfilled, (state, action) => {
        state.leadsLoading = false;
        state.leads = action.payload;
      })
      .addCase(fetchWebsiteLeads.rejected, (state, action) => {
        state.leadsLoading = false;
        state.error = action.payload?.error || "Failed to load leads";
      });

    // Quick publish (create + AI + deploy in one shot)
    builder
      .addCase(quickPublishWebsite.pending, (state) => {
        state.publishing = true;
        state.error = null;
      })
      .addCase(quickPublishWebsite.fulfilled, (state, action) => {
        state.publishing = false;
        state.currentWebsite = action.payload;
        state.successMsg = "Website published successfully!";
        state.wizardStep = 3;
      })
      .addCase(quickPublishWebsite.rejected, (state, action) => {
        state.publishing = false;
        state.error = action.payload?.error || "Failed to publish website. Please try again.";
      });

    // Delete website
    builder
      .addCase(deleteWebsite.fulfilled, (state, action) => {
        state.websites = state.websites.filter((w) => w.id !== action.payload);
        state.successMsg = "Website deleted";
      });

    // Edit website (change template + regenerate + redeploy)
    builder
      .addCase(editWebsite.pending, (state) => {
        state.editing = true;
        state.error = null;
      })
      .addCase(editWebsite.fulfilled, (state, action) => {
        state.editing = false;
        state.currentWebsite = action.payload;
        state.successMsg = "Website updated successfully!";
      })
      .addCase(editWebsite.rejected, (state, action) => {
        state.editing = false;
        state.error = action.payload?.error || "Failed to edit website. Please try again.";
      });
  },
});

export const { setWizardStep, clearCurrentWebsite, clearError, clearSuccess } =
  websiteBuilderSlice.actions;
export default websiteBuilderSlice.reducer;
